import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';

import { BaseSchemaModifierTransformation } from './base-schema-modifier-transformation';
import { ParseFormat, IDelimitedTextFormatSettings } from 'app/model/dataflow/parsetransformation';
import { ColumnExpressionProcessor } from './column-expression-processor';
import { DataFlowColumn } from '../dataset/dataflowcolumn';
import { IViolation } from '../base/annotation';
import { ComplexDataFlowType } from './dataflow-complex-datatype';
import { ArrayDataFlowType } from 'worker/app-workers/shared/models/dataflow-datatype';
import { ExpressionBasedColumn } from 'app/model/dataflow/expression-based.column';

@Metamodel.model({
    name: 'stringify',
    display: [stringResources.DataFlowStringifyTransformationLabel, stringResources.DataFlowStringifyTransformationsLabel],
    referable: true,
    constraints: {
        name: {
            required: true
        },
        expressionColumns: {
            validator: StringifyTransformation.validateNewColumns
        }
    }
})
export class StringifyTransformation extends BaseSchemaModifierTransformation {
    public static readonly MODEL: IModel;

    public format: ParseFormat = ParseFormat.json;
    public delimitedTextSettings: IDelimitedTextFormatSettings = {
        columnNamesAsHeader: false,
        columnDelimiter: ',',
        nullValue: ''
    };

    public constructor() {
        super();
        this.inspectSummaryOutputFields.splice(1, 0, {
            name: () => stringResources.TransformationInspect_Update,
            icon: () => 'svg-i-9x9-Update',
            value: () => this.overwrittenColumns.length
        });
        this.inspectDetailsOutputFields.unshift({
            name: () => this.columnInfoTitle,
            icon: this._getUpdatedIcon.bind(this)
        });
        this.className = 'Stringify';
    }

    public static validateNewColumns(transformation: BaseSchemaModifierTransformation): IViolation[] | undefined {
        const violations: IViolation[] = [];
        transformation.expressionColumns.forEach((col) => {
            if ((col as ExpressionBasedColumn).dataFlowExpression) {
                const colType = (col as ExpressionBasedColumn).dataFlowExpression.returnType;
                if (!(colType instanceof ComplexDataFlowType || (colType instanceof ArrayDataFlowType && colType.arrayType instanceof ComplexDataFlowType))) {
                    // Stringify expressions must be either complex or array of complex type
                    violations.push({
                        context: col,
                        message: () => stringResources.StringifyColumnTypeError
                    });
                }
            }
        });

        return violations;
    }

    public get columns(): DataFlowColumn[] {
        const generatedColumns = ColumnExpressionProcessor.processNewColumns(this.expressionColumns, this.distinctInputColumnRefs, this).map((processedColumnResult) => processedColumnResult.column);
        const columns: DataFlowColumn[] = [];
        this.overwrittenColumns = [];
        this.newColumns = [];
        this.unchangedColumns = [];
        for (const column of this.inputColumns) {
            const overwrittenColumnIndex = generatedColumns.findIndex((genColumn) => (genColumn.name) === column.name);
            if (overwrittenColumnIndex > -1) {
                const overwrittenColumn = generatedColumns[overwrittenColumnIndex];
                if (columns.indexOf(overwrittenColumn) < 0) {
                    columns.push(overwrittenColumn);
                }
                if (this.overwrittenColumns.indexOf(overwrittenColumn) < 0) {
                    this.overwrittenColumns.push(overwrittenColumn);
                }
            } else {
                this.unchangedColumns.push(column);
                columns.push(column);
            }
        }

        for (const column of generatedColumns) {
            if (columns.indexOf(column) < 0) {
                columns.push(column);
                if (this.overwrittenColumns.indexOf(column) < 0) {
                    this.newColumns.push(column);
                }
            }
        }
        return columns;
    }

    protected getGeneratedDescription(): string {
        return stringResources.StringifyTransformationDescription;
    }
}
